import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard, EmailGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { FaUniversity, FaPlus, FaFilePdf, FaFileCsv, FaTrash, FaTimes, FaCode, FaDollarSign } from 'react-icons/fa';
import { StandardURL } from "../App";

const Bank = () => {
  const [banks, setBanks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newBank, setNewBank] = useState({
    bank_code: "",
    bank_name: "",
  });
  const token = localStorage.getItem("token");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true); // Separate loading state for initial fetch

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = () => {
    const token = localStorage.getItem("token");
    setIsFetching(true);
    axios
      .get(`${StandardURL}/iNyumba/admin/bank/fetch`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        // Ensure response.data.response is an array, default to empty array
        setBanks(Array.isArray(response.data.response) ? response.data.response : []);
      })
      .catch((error) => {
        console.error("Error fetching banks:", error);
        toast.error(error.response?.data?.response || "Failed to fetch bank records.");
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBank({ ...newBank, [name]: value });
  };

  const handleAddBank = () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");
    
    axios
      .post(`${StandardURL}/iNyumba/admin/bank/add`, newBank, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setIsModalOpen(false);
        setNewBank({ bank_code: "", bank_name: "" });
        fetchBanks();
        toast.success(response.data.response || "Bank added successfully!");
      })
      .catch((error) => {
        console.error("Error adding bank:", error);
        toast.error(error.response?.data?.response || "Failed to add bank.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteBank = (bankCode) => {
    // Implement a simple confirmation before deleting
    if (!window.confirm(`Are you sure you want to delete bank with code: ${bankCode}?`)) {
        return;
    }

    setIsLoading(true);
    const token = localStorage.getItem("token");

    axios
      .delete(
        `${StandardURL}/iNyumba/admin/bank/delete/${bankCode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        fetchBanks();
        toast.success(response.data.response || `Bank with code ${bankCode} deleted successfully!`);
      })
      .catch((error) => {
        console.error("Error deleting bank:", error);
        toast.error(error.response?.data?.response || "Failed to delete bank.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownload = (fileType) => {
    const endpoint = `${StandardURL}/iNyumba/report/banksreports`;
    const downloadToast = toast.info("Preparing download...", { autoClose: false });

    axios
      .get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { format: fileType },
        responseType: "blob",
      })
      .then((response) => {
        const fileExtension = fileType === "pdf" ? "pdf" : "csv";
        const fileName = `inyumbabankrecords.${fileExtension}`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        
        toast.update(downloadToast, { 
            render: "Download successful!", 
            type: toast.TYPE.SUCCESS, 
            autoClose: 5000 
        });
      })
      .catch((error) => {
        console.error(`Error downloading ${fileType.toUpperCase()}:`, error);
        toast.update(downloadToast, { 
            render: error.response?.data?.response || `Failed to download ${fileType.toUpperCase()}.`, 
            type: toast.TYPE.ERROR, 
            autoClose: 5000 
        });
      });
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
           
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <h1 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center">
                    <FaUniversity className="mr-3 text-indigo-600" /> Bank Records Management
                </h1>

                {/* Action Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-4 shadow-lg rounded-xl mb-8 border-t-4 border-indigo-500">
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="w-full sm:w-auto inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md mb-3 sm:mb-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <FaPlus className="mr-2" /> Add New Bank
                    </button>
                    
                    <div className="flex space-x-3 w-full sm:w-auto">
                        <button
                            onClick={() => handleDownload("csv")}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            <FaFileCsv className="mr-2" /> Download CSV
                        </button>
                        <button
                            onClick={() => handleDownload("pdf")}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                            <FaFilePdf className="mr-2" /> Download PDF
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {isFetching && (
                    <div className="flex justify-center items-center py-10">
                        <Oval color="#4f46e5" secondaryColor="#6366f1" height={50} width={50} ariaLabel="loading-spinner" />
                        <p className="ml-4 text-gray-600">Fetching bank records...</p>
                    </div>
                )}

                {/* Bank Table Card */}
                {!isFetching && (
                    <div className="bg-white shadow-xl rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center">
                                            <FaCode className="mr-1"/> Bank Code
                                        </th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <FaUniversity className="mr-1 inline"/> Bank Name
                                        </th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {banks.length > 0 ? (
                                        banks.map((bank, index) => (
                                            <tr 
                                                key={bank.bankcode} 
                                                className={`transition duration-150 ${index % 2 === 0 ? 'hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}`}
                                            >
                                                <td className="py-4 px-6 whitespace-nowrap text-sm font-medium text-gray-900">{bank.bankcode}</td>
                                                <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-700">{bank.bankname}</td>
                                                <td className="py-4 px-6 whitespace-nowrap text-sm">
                                                    <button
                                                        onClick={() => handleDeleteBank(bank.bankcode)}
                                                        disabled={isLoading}
                                                        className={`inline-flex items-center justify-center py-2 px-4 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                                            isLoading
                                                                ? "bg-gray-400 text-white cursor-not-allowed"
                                                                : "bg-red-600 hover:bg-red-700 text-white focus:ring-red-500"
                                                        }`}
                                                    >
                                                        {isLoading ? (
                                                            <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="#d1d5db" />
                                                        ) : (
                                                            <>
                                                                <FaTrash className="mr-1.5 text-xs" /> Delete
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="py-8 text-center text-gray-500">No bank records found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
        
        {/* Add Bank Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full transform transition-all">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <FaPlus className="mr-2 text-indigo-600"/> Add New Bank
                    </h2>
                    <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition">
                        <FaTimes className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="bank_code" className="block text-sm font-medium text-gray-700 mb-1">Bank Code</label>
                        <input
                            type="text"
                            id="bank_code"
                            name="bank_code"
                            value={newBank.bank_code}
                            onChange={handleInputChange}
                            placeholder="e.g., 01001"
                            className="border border-gray-300 rounded-lg py-2 px-4 w-full focus:ring-indigo-500 focus:border-indigo-500 transition"
                        />
                    </div>
                    <div>
                        <label htmlFor="bank_name" className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                        <input
                            type="text"
                            id="bank_name"
                            name="bank_name"
                            value={newBank.bank_name}
                            onChange={handleInputChange}
                            placeholder="e.g., National Bank of Kenya"
                            className="border border-gray-300 rounded-lg py-2 px-4 w-full focus:ring-indigo-500 focus:border-indigo-500 transition"
                        />
                    </div>
                </div>

                <div className="flex justify-end mt-6 space-x-3">
                    <button
                        onClick={() => setIsModalOpen(false)}
                        className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300 transition duration-150"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAddBank}
                        disabled={isLoading}
                        className={`inline-flex items-center justify-center font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            isLoading
                                ? "bg-indigo-400 text-white cursor-not-allowed"
                                : "bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
                        }`}
                    >
                        {isLoading ? (
                            <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="#a5b4fc" />
                        ) : (
                            "Add Bank"
                        )}
                    </button>
                </div>
            </div>
          </div>
        )}
      </AuthGuard>
    </EmailGuard>
  );
};

export default Bank;